import type {
    AcademicData,
    AcademicYear,
    ActivityLog,
    AppUser,
    AttendanceRecord,
    AttendanceStatus,
    ClassRoom,
    ClassSubject,
    Gender,
    GradeRecord,
    GradeType,
    JournalEntry,
    Student,
    Subject
} from '@/types'

import { DEMO_TODAY, schoolDaysBetween } from '@/lib/dates'

// Sample school data. Generated from a fixed seed so the server and the browser
// always build exactly the same records.

export const ACTIVE_YEAR_ID = 'ta-2026-1'

const createRandom = (seed: number): (() => number) => {
    let state = seed >>> 0

    return (): number => {
        state = (state + 0x6d2b79f5) >>> 0
        let value = state
        value = Math.imul(value ^ (value >>> 15), value | 1)
        value ^= value + Math.imul(value ^ (value >>> 7), value | 61)

        return ((value ^ (value >>> 14)) >>> 0) / 4294967296
    }
}

const YEARS: AcademicYear[] = [
    { id: 'ta-2026-1', name: '2026/2027', semester: '1', startDate: '2026-07-13', endDate: '2026-12-18', active: true },
    { id: 'ta-2026-2', name: '2026/2027', semester: '2', startDate: '2027-01-04', endDate: '2027-06-25', active: false },
    { id: 'ta-2025-1', name: '2025/2026', semester: '1', startDate: '2025-07-14', endDate: '2025-12-19', active: false },
    { id: 'ta-2025-2', name: '2025/2026', semester: '2', startDate: '2026-01-05', endDate: '2026-06-26', active: false }
]

const CLASSES: ClassRoom[] = [
    { id: 'kls-x-rpl-1', name: 'X RPL 1' },
    { id: 'kls-x-tkj-1', name: 'X TKJ 1' },
    { id: 'kls-xi-rpl-1', name: 'XI RPL 1' },
    { id: 'kls-xii-rpl-1', name: 'XII RPL 1' }
]

const SUBJECTS: Subject[] = [
    { id: 'mpl-mtk', name: 'Matematika', kkm: 75 },
    { id: 'mpl-bind', name: 'Bahasa Indonesia', kkm: 75 },
    { id: 'mpl-bing', name: 'Bahasa Inggris', kkm: 75 },
    { id: 'mpl-pweb', name: 'Pemrograman Web', kkm: 70 },
    { id: 'mpl-bd', name: 'Basis Data', kkm: 72 },
    { id: 'mpl-ppkn', name: 'Pendidikan Pancasila', kkm: 78 }
]

const USERS: AppUser[] = [
    { id: 'usr-admin', name: 'Rina Kartika', username: 'admin', role: 'admin', active: true },
    { id: 'usr-budi', name: 'Budi Santoso', username: 'budi.santoso', role: 'guru', active: true },
    { id: 'usr-siti', name: 'Siti Rahmawati', username: 'siti.rahma', role: 'guru', active: true },
    { id: 'usr-maya', name: 'Maya Puspitasari', username: 'maya.puspita', role: 'guru', active: true },
    { id: 'usr-andi', name: 'Andi Wijaya', username: 'andi.wijaya', role: 'guru', active: false }
]

const ROSTER: { classId: string, nisStart: number, students: [string, Gender][] }[] = [
    {
        classId: 'kls-x-rpl-1',
        nisStart: 2026001,
        students: [
            ['Ahmad Fauzi', 'L'],
            ['Aisyah Putri Ramadhani', 'P'],
            ['Bagas Saputra', 'L'],
            ['Citra Lestari', 'P'],
            ['Dimas Prasetyo', 'L'],
            ['Fitri Handayani', 'P'],
            ['Galih Pratama', 'L'],
            ['Intan Permata Sari', 'P']
        ]
    },
    {
        classId: 'kls-x-tkj-1',
        nisStart: 2026101,
        students: [
            ['Annisa Rahmawati', 'P'],
            ['Dafa Alfarizi', 'L'],
            ['Keisha Amelia', 'P'],
            ['Naufal Hidayat', 'L'],
            ['Reza Pahlevi', 'L'],
            ['Vania Kirana', 'P'],
            ['Wahyu Setiawan', 'L'],
            ['Zahra Aulia', 'P']
        ]
    },
    {
        classId: 'kls-xi-rpl-1',
        nisStart: 2025001,
        students: [
            ['Dewi Anggraini', 'P'],
            ['Farhan Hakim', 'L'],
            ['Kevin Wijaya', 'L'],
            ['Nabila Zahra', 'P'],
            ['Putri Ayu Wulandari', 'P'],
            ['Rizky Maulana', 'L'],
            ['Salsabila Anindya', 'P'],
            ['Yoga Aditya', 'L']
        ]
    },
    {
        classId: 'kls-xii-rpl-1',
        nisStart: 2024001,
        students: [
            ['Arif Rahman', 'L'],
            ['Bima Nugraha', 'L'],
            ['Hendra Kurniawan', 'L'],
            ['Laras Setyaningrum', 'P'],
            ['Melati Kusuma', 'P'],
            ['Nadia Safitri', 'P'],
            ['Raka Firmansyah', 'L'],
            ['Tiara Ananda', 'P']
        ]
    }
]

const CLASS_SUBJECTS: ClassSubject[] = [
    { id: 'km-01', classId: 'kls-x-rpl-1', subjectId: 'mpl-mtk', teacherId: 'usr-budi' },
    { id: 'km-02', classId: 'kls-x-rpl-1', subjectId: 'mpl-bind', teacherId: 'usr-siti' },
    { id: 'km-03', classId: 'kls-x-rpl-1', subjectId: 'mpl-pweb', teacherId: 'usr-admin' },
    { id: 'km-04', classId: 'kls-x-tkj-1', subjectId: 'mpl-mtk', teacherId: 'usr-budi' },
    { id: 'km-05', classId: 'kls-x-tkj-1', subjectId: 'mpl-bing', teacherId: 'usr-maya' },
    { id: 'km-06', classId: 'kls-x-tkj-1', subjectId: 'mpl-ppkn', teacherId: 'usr-siti' },
    { id: 'km-07', classId: 'kls-xi-rpl-1', subjectId: 'mpl-pweb', teacherId: 'usr-admin' },
    { id: 'km-08', classId: 'kls-xi-rpl-1', subjectId: 'mpl-bd', teacherId: 'usr-admin' },
    { id: 'km-09', classId: 'kls-xi-rpl-1', subjectId: 'mpl-bing', teacherId: 'usr-maya' },
    { id: 'km-10', classId: 'kls-xii-rpl-1', subjectId: 'mpl-bd', teacherId: 'usr-admin' },
    { id: 'km-11', classId: 'kls-xii-rpl-1', subjectId: 'mpl-ppkn', teacherId: 'usr-siti' },
    { id: 'km-12', classId: 'kls-xii-rpl-1', subjectId: 'mpl-bind', teacherId: 'usr-siti' }
]

const TOPICS: Record<string, string[]> = {
    'mpl-mtk': [
        'Persamaan dan pertidaksamaan nilai mutlak',
        'Sistem persamaan linear tiga variabel',
        'Fungsi kuadrat dan grafiknya',
        'Barisan dan deret aritmetika',
        'Latihan soal dan pembahasan bersama'
    ],
    'mpl-bind': [
        'Teks laporan hasil observasi',
        'Menyusun paragraf eksposisi',
        'Membaca pemahaman dan ide pokok',
        'Teks negosiasi dalam kehidupan sehari-hari',
        'Presentasi kelompok dan umpan balik'
    ],
    'mpl-bing': [
        'Descriptive text: describing places',
        'Simple present and present continuous',
        'Making invitations and responses',
        'Reading comprehension: news items',
        'Speaking practice in pairs'
    ],
    'mpl-pweb': [
        'Struktur dasar HTML dan elemen semantik',
        'Mengatur tata letak dengan CSS Flexbox',
        'Layout responsif dengan CSS Grid',
        'Pengenalan JavaScript dan DOM',
        'Proyek mini halaman profil sekolah'
    ],
    'mpl-bd': [
        'Konsep entitas dan relasi (ERD)',
        'Normalisasi data hingga 3NF',
        'Perintah dasar SQL: SELECT dan WHERE',
        'Menggabungkan tabel dengan JOIN',
        'Studi kasus basis data perpustakaan'
    ],
    'mpl-ppkn': [
        'Nilai-nilai Pancasila dalam kehidupan sehari-hari',
        'Hak dan kewajiban warga negara',
        'Musyawarah dan pengambilan keputusan',
        'Keberagaman dalam bingkai NKRI',
        'Diskusi kasus dan refleksi kelompok'
    ]
}

// Students who show up in "Siswa perlu diperhatikan"
const FREQUENT_ABSENCE = new Set(['2026007', '2025003', '2026105'])
const OFTEN_SICK = new Set(['2024004', '2026103'])

const pickStatus = (roll: number, nis: string): AttendanceStatus => {
    if (FREQUENT_ABSENCE.has(nis)) {
        if (roll < 0.78) return 'H'
        if (roll < 0.83) return 'S'
        if (roll < 0.87) return 'I'

        return 'A'
    }

    if (OFTEN_SICK.has(nis)) {
        if (roll < 0.84) return 'H'
        if (roll < 0.95) return 'S'
        if (roll < 0.98) return 'I'

        return 'A'
    }

    if (roll < 0.92) return 'H'
    if (roll < 0.955) return 'S'
    if (roll < 0.985) return 'I'

    return 'A'
}

const PERIODS = [
    { period: '1-2', start: '07:00', end: '08:20' },
    { period: '3-4', start: '08:20', end: '09:40' }
]

const LOGS: ActivityLog[] = [
    { id: 'log-15', userId: 'usr-siti', action: 'save', module: 'absensi', description: 'Menyimpan absensi kelas XII RPL 1', ip: '10.12.4.37', createdAt: '2026-11-04T01:40:00.000Z' },
    { id: 'log-14', userId: 'usr-budi', action: 'bulk', module: 'absensi', description: 'Bulk absensi H untuk 8 siswa', ip: '10.12.4.52', createdAt: '2026-11-04T00:26:00.000Z' },
    { id: 'log-13', userId: 'usr-admin', action: 'save', module: 'absensi', description: 'Menyimpan absensi kelas XI RPL 1', ip: '10.12.4.21', createdAt: '2026-11-04T00:18:00.000Z' },
    { id: 'log-12', userId: 'usr-admin', action: 'save', module: 'nilai', description: 'Menyimpan nilai', ip: '10.12.4.21', createdAt: '2026-11-03T07:15:00.000Z' },
    { id: 'log-11', userId: 'usr-budi', action: 'save', module: 'siswa', description: 'Menyimpan siswa Dimas Prasetyo', ip: '10.12.4.52', createdAt: '2026-11-03T03:02:00.000Z' },
    { id: 'log-10', userId: 'usr-maya', action: 'save', module: 'nilai', description: 'Menyimpan nilai', ip: '10.12.4.44', createdAt: '2026-11-02T08:32:00.000Z' },
    { id: 'log-09', userId: 'usr-admin', action: 'save', module: 'siswa', description: 'Menyimpan siswa Zahra Aulia', ip: '10.12.4.21', createdAt: '2026-11-02T01:45:00.000Z' },
    { id: 'log-08', userId: 'usr-admin', action: 'import', module: 'nilai', description: 'Mengimpor 8 nilai', ip: '10.12.4.21', createdAt: '2026-10-29T02:30:00.000Z' },
    { id: 'log-07', userId: 'usr-admin', action: 'assign', module: 'mapel', description: 'Assign mapel ke kelas', ip: '10.12.4.21', createdAt: '2026-10-28T01:00:00.000Z' },
    { id: 'log-06', userId: 'usr-admin', action: 'create', module: 'pengguna', description: 'Menambah pengguna maya.puspita', ip: '10.12.4.21', createdAt: '2026-10-27T06:40:00.000Z' },
    { id: 'log-05', userId: 'usr-admin', action: 'update', module: 'pengguna', description: 'Memperbarui pengguna andi.wijaya', ip: '10.12.4.21', createdAt: '2026-10-20T04:12:00.000Z' },
    { id: 'log-04', userId: 'usr-admin', action: 'import', module: 'siswa', description: 'Mengimpor 8 siswa', ip: '10.12.4.21', createdAt: '2026-07-14T02:05:00.000Z' },
    { id: 'log-03', userId: 'usr-admin', action: 'activate', module: 'tahun_ajaran', description: 'Mengaktifkan tahun ajaran 2026/2027 · Semester 1', ip: '10.12.4.21', createdAt: '2026-07-13T00:30:00.000Z' },
    { id: 'log-02', userId: 'usr-admin', action: 'create', module: 'tahun_ajaran', description: 'Menambah tahun ajaran 2026/2027 dengan Semester 1 dan Semester 2', ip: '10.12.4.21', createdAt: '2026-07-10T03:15:00.000Z' },
    { id: 'log-01', userId: 'usr-admin', action: 'delete', module: 'tahun_ajaran', description: 'Menghapus tahun ajaran 2024/2025', ip: '10.12.4.21', createdAt: '2026-07-10T03:10:00.000Z' }
]

export const createSeedData = (): AcademicData => {
    const random = createRandom(20261104)

    const students: Student[] = ROSTER.flatMap(group => group.students.map(([name, gender], index) => {
        const nis = String(group.nisStart + index)

        return { id: `sis-${nis}`, classId: group.classId, nis, name, gender }
    }))

    const schoolDays = schoolDaysBetween('2026-09-01', DEMO_TODAY)

    // Attendance: every school day, except one class that has not been recorded today
    const attendance: AttendanceRecord[] = []

    schoolDays.forEach(date => {
        students.forEach(student => {
            if (date === DEMO_TODAY && student.classId === 'kls-x-tkj-1') return

            attendance.push({
                id: `abs-${student.nis}-${date}`,
                yearId: ACTIVE_YEAR_ID,
                studentId: student.id,
                classId: student.classId,
                date,
                status: pickStatus(random(), student.nis)
            })
        })
    })

    // Grades: three daily tasks, one quiz and the mid-semester exam per assigned subject
    const ability = new Map(students.map(student => [student.id, 66 + random() * 28]))
    const grades: GradeRecord[] = []

    CLASS_SUBJECTS.forEach((assignment, assignmentIndex) => {
        const subject = SUBJECTS.find(item => item.id === assignment.subjectId)
        const subjectIndex = SUBJECTS.findIndex(item => item.id === assignment.subjectId)

        if (!subject) return

        const schedule: { type: GradeType, date: string }[] = [
            { type: 'harian', date: schoolDays[4 + subjectIndex] },
            { type: 'tengah', date: schoolDays[18 + (assignmentIndex % 3)] },
            { type: 'harian', date: schoolDays[24 + subjectIndex] },
            { type: 'ulangan', date: schoolDays[29 + subjectIndex] },
            { type: 'harian', date: schoolDays[38 + subjectIndex] }
        ]

        const classStudents = students.filter(student => student.classId === assignment.classId)
        const subjectBias = random() * 12 - 6

        schedule.forEach(({ type, date }) => {
            classStudents.forEach(student => {
                const base = ability.get(student.id) ?? 75
                const noise = random() * 14 - 7
                const score = Math.max(45, Math.min(100, Math.round(base + subjectBias + noise)))

                grades.push({
                    id: `nil-${student.nis}-${subject.id}-${type}-${date}`,
                    yearId: ACTIVE_YEAR_ID,
                    studentId: student.id,
                    subjectId: subject.id,
                    type,
                    date,
                    score,
                    kkm: subject.kkm
                })
            })
        })
    })

    // Teaching journal for the last two weeks
    const journals: JournalEntry[] = []
    const topicCursor = new Map<string, number>()
    const recentDays = schoolDays.slice(-10)

    recentDays.forEach((date, dayIndex) => {
        CLASSES.forEach((classRoom, classIndex) => {
            if (date === DEMO_TODAY && (classRoom.id === 'kls-x-tkj-1' || classRoom.id === 'kls-xii-rpl-1')) return

            const assigned = CLASS_SUBJECTS.filter(item => item.classId === classRoom.id)
            const sessions = (dayIndex + classIndex) % 3 === 0 ? 2 : 1

            for (let session = 0; session < sessions; session += 1) {
                const assignment = assigned[(dayIndex + classIndex + session) % assigned.length]
                const subject = SUBJECTS.find(item => item.id === assignment.subjectId)

                if (!subject) continue

                const cursor = topicCursor.get(`${classRoom.id}-${subject.id}`) ?? 0
                const topics = TOPICS[subject.id]
                topicCursor.set(`${classRoom.id}-${subject.id}`, cursor + 1)

                journals.push({
                    id: `jur-${classRoom.id}-${date}-${session}`,
                    yearId: ACTIVE_YEAR_ID,
                    classId: classRoom.id,
                    date,
                    period: PERIODS[session].period,
                    start: PERIODS[session].start,
                    end: PERIODS[session].end,
                    material: `${subject.name}: ${topics[cursor % topics.length]}`
                })
            }
        })
    })

    return {
        classes: CLASSES,
        subjects: SUBJECTS,
        students,
        attendance,
        grades,
        journals,
        years: YEARS,
        users: USERS,
        logs: LOGS,
        classSubjects: CLASS_SUBJECTS,
        settings: {
            appName: 'Absensi & Nilai',
            tagline: 'Portal Akademik',
            logoUrl: null,
            logoSize: 40,
            showLogo: true
        },
        currentUserId: 'usr-admin',
        selectedYearId: ACTIVE_YEAR_ID
    }
}
